import React, { useState, useRef, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Wallet, ChevronDown, LogOut, RefreshCw, Copy, CheckCircle, 
  ExternalLink, Repeat
} from 'lucide-react';

const WalletMenu = ({ onSwitchWallet }) => {
  const { account, bethBalance, walletType, disconnectWallet, refreshData } = useWeb3();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!account) return null;

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDisconnect = () => {
    disconnectWallet();
    toast.success('Wallet disconnected');
    setIsOpen(false);
  };

  const handleSwitchWallet = () => {
    setIsOpen(false);
    if (onSwitchWallet) {
      onSwitchWallet();
    }
  };

  const handleRefresh = async () => {
    if (refreshData) {
      await refreshData();
      toast.success('Data refreshed');
    }
    setIsOpen(false);
  };

  const formatBalance = (bal) => {
    if (!bal) return '0';
    const num = parseFloat(bal);
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
    return num.toFixed(4);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Wallet Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-[#1a2d4a] rounded-lg border border-[#2a3d5a] hover:border-[#b8860b]/50 transition-colors"
        data-testid="wallet-menu-btn"
      >
        <div className="w-6 h-6 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
          <Wallet className="w-3 h-3 text-[#b8860b]" />
        </div>
        <div className="text-left">
          <p className="text-white text-sm font-mono">
            {account.slice(0, 6)}...{account.slice(-4)}
          </p>
          <p className="text-gray-500 text-xs">
            {walletType === 'walletconnect' ? 'WalletConnect' : 'MetaMask'}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-[#0d1d35] border border-[#1a2d4a] rounded-xl shadow-xl z-50">
          {/* Header */}
          <div className="p-4 border-b border-[#1a2d4a]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs">Connected Wallet</p>
              <span className={`px-2 py-0.5 text-xs rounded ${walletType === 'walletconnect' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                {walletType === 'walletconnect' ? 'WalletConnect' : 'MetaMask'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-white font-mono text-sm">{account.slice(0, 10)}...{account.slice(-8)}</p>
              <button 
                onClick={copyAddress}
                className="p-1.5 hover:bg-[#1a2d4a] rounded transition-colors"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Balance */}
          <div className="p-4 border-b border-[#1a2d4a]">
            <p className="text-gray-400 text-xs mb-1">BETH Balance</p>
            <p className="text-white text-xl font-bold">{formatBalance(bethBalance)} BETH</p>
          </div>

          {/* Actions */}
          <div className="p-2">
            <button
              onClick={handleRefresh}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-300 hover:bg-[#1a2d4a] rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4 text-[#b8860b]" />
              <span className="text-sm">Refresh Balance</span>
            </button>

            <button
              onClick={handleSwitchWallet}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-300 hover:bg-[#1a2d4a] rounded-lg transition-colors"
              data-testid="switch-wallet-btn"
            >
              <Repeat className="w-4 h-4 text-[#b8860b]" />
              <span className="text-sm">Switch Wallet</span>
            </button>

            <a
              href={`https://etherscan.io/address/${account}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-300 hover:bg-[#1a2d4a] rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-[#b8860b]" />
              <span className="text-sm">View on Etherscan</span>
            </a>

            <button
              onClick={handleDisconnect}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              data-testid="disconnect-wallet-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Disconnect Wallet</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletMenu;
